import { isActive, useEligibility, useRuns, type Run } from "@/app/gradingQueries";
import type { Task, TaskStatus } from "@/kernel/runner";
import { errorInfo } from "@/routes/step/model";
import { CELL, CELL_LOOK, STATUS_WORD } from "./AutocheckGrid";

/** С телефона не отправляют (правило 9) — кнопки нет, но причина видна всегда. */
export const DESKTOP_ONLY = "Отправить работу ментору можно с компьютера.";

const HEAD = "m-0 font-mono text-[10.5px] font-normal text-mut";

/** Счётчик: число — цветом статуса, подпись — --ink2 (на карточке). */
function Counter({ id, value, label, tone }: { id: string; value: number; label: string; tone: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-[4px] border border-bd bg-bg2 px-2 py-1.5">
      <span className={`font-mono text-[15px] font-semibold leading-none ${tone}`} data-testid={id}>
        {value}
      </span>
      <span className="font-mono text-[10.5px] text-ink2">{label}</span>
    </div>
  );
}

function Grid({ run, tasks }: { run: Run; tasks: Task[] }) {
  const byN = new Map((run.result?.tasks ?? []).map((t) => [t.n, t]));
  return (
    <ul className="m-0 grid list-none grid-cols-7 gap-[5px] p-0" aria-label="серверная автопроверка по задачам" data-testid="server-grid">
      {tasks.map((t) => {
        const r = byN.get(t.n);
        const s: TaskStatus = r?.status ?? "idle";
        const title = `Задача ${t.n}: ${t.title}${s === "failed" && r?.error_kind ? ` — ${r.error_kind}` : ""}`;
        return (
          <li key={t.n} title={title} data-status={s} className={`${CELL} ${CELL_LOOK[s]}`}>
            {t.n}
            <span className="sr-only"> — {STATUS_WORD[s]}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Причина из условий отправки (правило 3) и честное «с компьютера». */
function Reason({ assignmentId }: { assignmentId: string }) {
  const e = useEligibility(assignmentId);
  let text: string;
  let desktop = true;
  if (e.status === "success") {
    if (e.data.submitted) {
      text = "Работа на проверке у ментора.";
      desktop = false;
    } else if (e.data.eligible) text = "Условия выполнены.";
    else text = e.data.reason;
  } else if (e.status === "error") text = "Условия отправки не загрузились — их видно с компьютера.";
  else text = "проверяем условия отправки…";
  return (
    <div className="border-t border-bd pt-2.5 text-[12.5px] leading-[1.5] text-ink2">
      <div className="mb-1 font-mono text-[10.5px] text-mut">отправка</div>
      <p className="m-0 text-pretty" data-testid="server-reason">
        {text}
      </p>
      {desktop && <p className="m-0 mt-1 text-pretty">{DESKTOP_ONLY}</p>}
    </div>
  );
}

/** S8: итог последнего серверного прогона, только чтение. */
export function ServerCheck({ assignmentId, tasks }: { assignmentId: string; tasks: Task[] }) {
  const runs = useRuns(assignmentId);
  const latest = runs.data?.[0];

  let body;
  if (runs.status === "pending") {
    body = <p className="m-0 font-mono text-[11.5px] text-ink2">загружаем прогоны…</p>;
  } else if (runs.status === "error") {
    body = (
      <div className="flex flex-col items-start gap-2" role="alert">
        <p className="m-0 text-pretty text-[12.5px] leading-[1.5] text-ink2">Прогоны не загрузились: {errorInfo(runs.error).message} Черновик и результаты на сервере не пропали.</p>
        <button type="button" onClick={() => void runs.refetch()} className="cursor-pointer rounded-[4px] border border-bd2 bg-transparent px-[11px] py-[6px] font-mono text-[11px] text-ink hover:border-acc hover:text-acc">
          повторить
        </button>
      </div>
    );
  } else if (!latest) {
    body = <p className="m-0 text-pretty text-[12.5px] leading-[1.5] text-ink2">прогонов ещё не было — проверка запускается с компьютера</p>;
  } else if (isActive(latest)) {
    body = <p className="m-0 font-mono text-[11.5px] text-ink2">прогон #{latest.n} идёт — раннер выполняет ячейки…</p>;
  } else if (latest.status === "error" || latest.result?.env_error) {
    body = (
      <p className="m-0 text-pretty text-[12.5px] leading-[1.5] text-err" data-testid="server-env-error">
        ошибка среды: {latest.result?.env_error?.message ?? "прогон не завершился, результата нет."}
      </p>
    );
  } else {
    body = (
      <>
        <div className="grid grid-cols-3 gap-[5px]">
          <Counter id="server-passed" value={latest.passed} label="прошло" tone="text-ok" />
          <Counter id="server-failed" value={latest.failed} label="упало" tone="text-err" />
          <Counter id="server-idle" value={latest.idle} label="не запускалось" tone="text-ink2" />
        </div>
        <Grid run={latest} tasks={tasks} />
      </>
    );
  }

  const done = latest && !isActive(latest);
  return (
    <section className="flex flex-col gap-2.5 rounded-md border border-bd bg-card p-3" data-testid="server-check">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className={HEAD}>проверка на сервере</h2>
        {done && latest.status === "done" && (
          <span className="font-mono text-[11px] text-ink2" data-testid="server-run-meta">
            прогон #{latest.n} · {latest.passed} / {latest.total}
          </span>
        )}
      </div>
      {body}
      {runs.status === "success" && latest && !isActive(latest) && <Reason assignmentId={assignmentId} />}
    </section>
  );
}
